import './StudioView.scss';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import { push } from 'react-router-redux';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import MomentUtils from '@date-io/moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import BookingForm from './BookingForm';
import queryString from 'query-string';
import {
  Link,
  Paper,
  Divider,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  CardActions,
  IconButton,
  Grid,
  Typography,
  Avatar,
  Collapse,
  Button,
  Chip,
  SnackbarContent,
  Snackbar,
  WithStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import CalendarToday from '@material-ui/icons/CalendarToday';
import DateRange from '@material-ui/icons/DateRange';
import Timer from '@material-ui/icons/Timer';
import Notifications from '@material-ui/icons/Notifications';
import moment from 'moment';

interface MatchParams {
  domainName?: string;
  id?: string;
  courseId: string;
}

interface Teacher {
  _id: string;
  name: string;
  avatar?: string;
  [key: string]: any;
}

interface Course {
  _id: string;
  title: string;
  description?: string;
  avatar?: string;
  billingTag?: string;
  private: boolean;
  allowBookEmptySlots: boolean;
  beforeBookingText?: string;
  [key: string]: any;
}

interface Studio {
  id: string;
  _id: string;
  owner_id: string;
  name: string;
  address?: string;
  avatar?: string;
  domainName?: string;
  bookFrom: number;
  bookTo: number;
  pricing?: string;
  [key: string]: any;
}

interface Owner {
  _id: string;
  name: string;
  timezone: string;
  [key: string]: any;
}

interface Room {
  _id: string;
  name: string;
  [key: string]: any;
}

interface RepeatableEvent {
  _id: string;
  [key: string]: any;
}

interface Schedule {
  rooms: Room[];
  repeatableEvents: RepeatableEvent[];
  roomsMap: Record<string, Room>;
  [key: string]: any;
}

interface Event {
  classDateTime: string;
  [key: string]: any;
}

interface AlternativeCourseData {
  course: Course;
  teacher?: Teacher;
  classDates: Event[];
  [key: string]: any;
}

interface BookingCourseViewProps extends RouteComponentProps<MatchParams>, WithStyles<typeof styles> {
  translate: (key: string, data?: any) => string;
  currentLanguage: string;
  user: any;
  studio: Studio;
  domain: {
    id: string;
    currencyCode: string;
  };
  date: moment.Moment;
  registerToClass: (studio: Studio, classId: string) => void;
  toClassSignUp: (link: string) => void;
  clientAttendance: (studio: Studio, groupId: string, attendanceId: string, client: { new: boolean }) => void;
}

interface BookingCourseViewState {
  studio?: Studio;
  owner?: Owner;
  notifications: any[];
  showFullDescription: boolean;
  showFullPricing: boolean;
  showSubscribeConfirm: boolean;
  showSubscribeConfirmed: boolean;
  showUnsubscribeConfirm: boolean;
  showUnsubscribeConfirmed: boolean;
  showRegisterToClass: boolean;
  repeatableClass?: any;
  categoryLinks: any[];
  reviews: any[];
  coursesExpanded: Record<string, boolean>;
  courseEvents: Record<string, any>;
  to: moment.Moment;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  date: moment.Moment;
  events?: Event[];
  selectedEvent?: Event;
  alternativeCourses?: AlternativeCourseData[];
  showAlternatives?: boolean;
  loadingAlternatives?: boolean;
  course?: Course;
  teacher?: Teacher;
  rooms?: Room[];
  repeatableEvents?: RepeatableEvent[];
  roomsMap?: Record<string, Room>;
  schedule?: Schedule;
  teachers?: Teacher[];
  teachersMap?: Record<string, Teacher>;
  showErrorSnackbar?: boolean;
  showPricingDetails?: boolean;
}

declare global {
  interface Window {
    __PAGE_STATE__?: {
      studio?: Studio;
      studioOwner?: Owner;
      course?: Course;
    };
  }
}

class BookingCourseView extends React.Component<BookingCourseViewProps, BookingCourseViewState> {
  constructor(props: BookingCourseViewProps) {
    super(props);
    const pageState = window.__PAGE_STATE__;
    this.state = {
      studio: pageState && pageState.studio,
      owner: pageState && pageState.studioOwner,
      notifications: [],
      showFullDescription: false,
      showFullPricing: false,
      showSubscribeConfirm: false,
      showSubscribeConfirmed: false,
      showUnsubscribeConfirm: false,
      showUnsubscribeConfirmed: false,
      showRegisterToClass: false,
      repeatableClass: undefined,
      categoryLinks: [],
      reviews: [],
      coursesExpanded: {},
      courseEvents: {},
      to: moment.utc().add(1, 'days').endOf('isoWeek'),
      minDate: moment.utc().add(1, 'days'),
      maxDate: moment.utc().add(1, 'weeks').add(1, 'days'),
      date: props.date,
    };
  }

  componentDidMount() {
    if (!this.state.studio) {
      let api = serviceLocator.studioApi;
      let domainName = this.props.match.params.domainName;
      if (domainName) {
        return api.getStudioByDomainName(domainName).then(this.onSuccess, LayoutHelper.onErr);
      } else {
        let id = this.props.match.params.id;
        if (id) {
          return api.getStudioById(id).then(this.onSuccess, LayoutHelper.onErr);
        }
      }
    } else {
      this.initDates(this.state.studio, this.state.owner as Owner);
    }
  }

  loadSchedule = (studio: Studio): void => {
    let api = serviceLocator.studioApi;
    api.getPublicSchedule(studio.id).then((response: Schedule) => {
      let schedule = response;
      let rooms = schedule.rooms;
      let repeatableEvents = schedule.repeatableEvents;
      let roomsMap = schedule.roomsMap;
      this.setState({
        studio: studio,
        schedule: schedule,
        rooms: rooms,
        repeatableEvents: repeatableEvents,
        roomsMap: roomsMap,
      });
    }, LayoutHelper.onErr);
  };

  loadEvents = (studio: Studio, courseId: string, date: moment.Moment, owner: Owner): void => {
    this.setState({
      events: undefined,
      selectedEvent: undefined,
      alternativeCourses: undefined,
    });
    serviceLocator.registrationApi
      .getFreeSlotsOnCourse(studio._id, courseId, date, owner.timezone)
      .then((events: { classDates: Event[]; studio: Studio; course: Course; teacher: Teacher }) => {
        let freeSlots = events.classDates;

        let changeSet: Partial<BookingCourseViewState> = { events: freeSlots };
        if (freeSlots && freeSlots.length <= 2) {
          changeSet.showAlternatives = true;
        } else {
          changeSet.showAlternatives = false;
        }
        if (!this.state.studio) {
          changeSet.studio = events.studio;
        }

        if (!this.state.course) {
          changeSet.course = events.course;
        } else {
          changeSet.course = this.state.course;
        }
        if (!this.state.teacher) {
          changeSet.teacher = events.teacher;
        }
        this.setState(changeSet as BookingCourseViewState);
        if (changeSet.course) {
          this.loadAltrnatives(studio, changeSet.course, date, this.state.owner as Owner);
        }
      });
  };

  loadAltrnatives = (studio: Studio, course: Course, date: moment.Moment, owner: Owner): void => {
    this.setState({ loadingAlternatives: true });
    serviceLocator.registrationApi
      .getAlternativeFreeSlots(studio._id, course.billingTag as string, course._id, date, owner.timezone)
      .then((alternativeCourses: AlternativeCourseData[]) => {
        this.setState({
          alternativeCourses: alternativeCourses,
          loadingAlternatives: false,
        });
      }, LayoutHelper.onErr);
  };

  loadTeachers = (studio: Studio): void => {
    let api = serviceLocator.teacherApi;

    api.getPublicTeachers(studio.owner_id).then((data: { teachers: Teacher[] }) => {
      let teachers = data.teachers;
      let teachersMap: Record<string, Teacher> = {};
      for (let i = 0; i < teachers.length; i++) {
        let teacher = teachers[i];
        teachersMap[teacher._id] = teacher;
      }
      this.setState({ teachers: teachers, teachersMap });
      this.loadSchedule(studio);
    }, LayoutHelper.onErr);
  };

  onSuccess = (studio: Studio): void => {
    this.loadOwnerInfo(studio);
  };

  loadOwnerInfo = (studio: Studio): void => {
    serviceLocator.userApi.getOwnerInfo(studio.owner_id).then((owner: Owner) => {
      this.setState({ owner: owner });
      this.initDates(studio, owner);
    }, LayoutHelper.onErr);
  };

  initDates = (studio: Studio, owner: Owner): void => {
    let date = this.props.date;
    const utcOffset = moment.tz(owner.timezone).utcOffset() / 60;
    let minDate = moment
      .utc()
      .add(studio.bookFrom + utcOffset, 'hours')
      .startOf('day');
    let maxDate = moment
      .utc()
      .add(studio.bookTo + utcOffset, 'hours')
      .startOf('day');
    if (date.diff(minDate) < 0 || date.diff(maxDate) > 0) {
      date = minDate;
    }
    this.setState({ date: date, minDate: minDate, maxDate: maxDate });
    this.loadEvents(studio, this.props.match.params.courseId, date, owner);
  };

  render(): JSX.Element {
    let { classes } = this.props;

    return (
      <main className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {this.renderRepeatableClass(this.state.course)}
          </Grid>
          <Grid item xs={12} md={5}>
            {this.renderTimeSelect()}
          </Grid>
          <Grid item xs={12} md={3}>
            <Divider />
            {this.renderAlternativeCourses()}
          </Grid>
        </Grid>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={this.state.showErrorSnackbar}
          onClose={() => {
            this.setState({ showErrorSnackbar: false });
          }}
        >
          <SnackbarContent
            message={this.props.translate('manageRegistration.bookingError')}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={() => {
                  this.setState({ showErrorSnackbar: false });
                }}
              >
                <CloseIcon />
              </IconButton>,
            ]}
            style={{
              backgroundColor: '#d32f2f',
            }}
          />
        </Snackbar>
      </main>
    );
  }

  renderTimeSelect = (): JSX.Element | null => {
    let { date, minDate, maxDate } = this.state;
    let course = this.state.course;
    if (course) {
      if (!course.private && course.allowBookEmptySlots) {
        return (
          <Fragment>
            <Paper className={this.props.classes.timeSelect}>
              <List>
                <ListItem>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
                        <DatePicker
                          leftArrowIcon={<KeyboardArrowLeft />}
                          rightArrowIcon={<KeyboardArrowRight />}
                          keyboardIcon={<CalendarToday />}
                          dateRangeIcon={<DateRange />}
                          timeIcon={<Timer />}
                          minDate={this.state.minDate}
                          maxDate={this.state.maxDate}
                          format="DD MMM YYYY"
                          autoOk
                          inputVariant="outlined"
                          label={this.props.translate('manageRegistration.classDate')}
                          variant="inline"
                          value={this.state.date}
                          onChange={(date: moment.Moment) => {
                            this.setState({ date: date });
                            this.loadEvents(
                              this.state.studio as Studio,
                              (this.state.course as Course)._id,
                              date,
                              this.state.owner as Owner
                            );
                          }}
                        />
                      </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid item>
                      <Typography>
                        {this.props.translate('manageRegistration.hostTimeZoneNotification')}:{' '}
                        {this.state.owner && moment.tz(this.state.owner.timezone).format('Z z')}
                      </Typography>
                    </Grid>
                    {this.state.course.beforeBookingText && (
                      <Grid item xs={12}>
                        <Typography
                          dangerouslySetInnerHTML={{
                            __html: this.state.course.beforeBookingText,
                          }}
                        ></Typography>
                      </Grid>
                    )}
                  </Grid>
                </ListItem>
                <Divider />
                <ListItem>{this.renderClasses(course)}</ListItem>
              </List>
            </Paper>

            {this.state.selectedEvent && (
              <BookingForm
                studio={this.state.studio as Studio}
                courseId={(this.state.course as Course)._id}
                domain={this.props.domain}
                buttonTitle={`${this.props.translate('studio.class.signUp')} ${moment
                  .utc(this.state.selectedEvent.classDateTime)
                  .format('DD MMM YYYY HH:mm')}`}
                onSubmit={async (values: any) => {
                  try {
                    const attendanceData = await serviceLocator.registrationApi.createFreeSlotOnCourse(
                      (this.state.studio as Studio).owner_id,
                      (this.state.studio as Studio)._id,
                      (this.state.course as Course)._id,
                      this.state.selectedEvent,
                      values
                    );
                    const attendance = attendanceData.billedAttendance;
                    const studioClient = attendanceData.studioClient;
                    this.props.clientAttendance(
                      this.state.studio as Studio,
                      attendance.groupId,
                      attendance._id,
                      studioClient
                    );
                  } catch (e) {
                    this.setState({ showErrorSnackbar: true });
                    this.loadEvents(
                      this.state.studio as Studio,
                      (this.state.course as Course)._id,
                      this.state.date,
                      this.state.owner as Owner
                    );
                  }
                }}
                owner={this.state.owner as Owner}
              />
            )}
          </Fragment>
        );
      }
    }
    return null;
  };

  renderAlternativeCourses = (): JSX.Element | null => {
    if (this.state.loadingAlternatives) {
      return LayoutHelper.renderLoader();
    }
    if (this.state.alternativeCourses && this.state.alternativeCourses.length > 0) {
      return (
        <Grid container spacing={1} className={this.props.classes.alternativeCourses}>
          <Grid item xs={12}>
            <Typography component="h5">{this.props.translate('manageRegistration.alternativeCourses')}</Typography>
          </Grid>

          {this.state.alternativeCourses.map((alternativeCourse, index) => {
            return this.renderAlternativeCourse(alternativeCourse);
          })}
        </Grid>
      );
    }
    return null;
  };

  renderAlternativeCourse = (alternativeCourseData: AlternativeCourseData): JSX.Element | null => {
    let course = alternativeCourseData.course;
    let teacher = alternativeCourseData.teacher;
    let classDates = alternativeCourseData.classDates;
    if (classDates) {
      return (
        <Grid item xs={12} key={course._id}>
          <Card>
            <CardHeader
              avatar={<Avatar src={course.avatar || (teacher && teacher.avatar)} />}
              title={course.title}
              subheader={teacher && teacher.name}
            ></CardHeader>
            <Divider />
            <CardContent>
              {classDates.length === 0 ? (
                <Typography>{this.props.translate('manageRegistration.noClassesAvailable')}</Typography>
              ) : (
                classDates.map((freeSlot) => {
                  let classDateTime = moment.utc(freeSlot.classDateTime);
                  return (
                    <Chip
                      key={freeSlot.classDateTime}
                      label={classDateTime.format('HH:mm')}
                      onClick={() => {
                        this.goToAlternativeFreeSlot(alternativeCourseData, freeSlot);
                      }}
                    />
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>
      );
    }
    return null;
  };

  goToAlternativeFreeSlot = (alternativeCourseData: AlternativeCourseData, freeSlot: Event): void => {
    let course = alternativeCourseData.course;
    let teacher = alternativeCourseData.teacher;
    let classDates = alternativeCourseData.classDates;
    this.loadAltrnatives(this.state.studio as Studio, course, this.state.date, this.state.owner as Owner);
    this.setState({
      course: course,
      teacher: teacher,
      selectedEvent: freeSlot,
      events: classDates,
      showAlternatives: false,
    });
  };

  renderRepeatableClass = (course?: Course): JSX.Element | React.ReactNode => {
    if (course) {
      if (!course.private && course.allowBookEmptySlots) {
        return this.renderCourseHeader(course);
      } else {
        return this.props.translate('manageRegistration.courseIsNotAvailable');
      }
    } else {
      return LayoutHelper.renderLoader();
    }
  };

  renderCourseHeader = (course: Course): JSX.Element => {
    let teacher = this.state.teacher;
    return (
      <Card>
        <CardHeader
          avatar={<Avatar src={(this.state.studio as Studio).avatar} className={this.props.classes.avatar}></Avatar>}
          title={
            <Link href={routes.getStudioLink(this.state.studio as Studio)}> {(this.state.studio as Studio).name}</Link>
          }
          subheader={(this.state.studio as Studio).address}
        ></CardHeader>
        <CardHeader
          avatar={
            <Avatar src={course.avatar || (teacher && teacher.avatar)} className={this.props.classes.bigAvatar} />
          }
          title={course.title}
          subheader={teacher && teacher.name}
        ></CardHeader>

        <CardContent>{this.renderCourseDescription(course)}</CardContent>
        <CardActions>
          <Button
            size="small"
            color="secondary"
            onClick={() => {
              this.setState({
                showPricingDetails: !this.state.showPricingDetails,
              });
            }}
          >
            <Notifications />
            {this.props.translate('studio.pricingDetails')}
          </Button>
        </CardActions>
        <Collapse in={this.state.showPricingDetails} timeout="auto">
          <CardContent>
            <div dangerouslySetInnerHTML={{ __html: (this.state.studio as Studio).pricing || '' }} className="text" />
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  renderClasses = (course: Course): JSX.Element => {
    if (!this.state.events) {
      return LayoutHelper.renderLoader();
    }
    if (this.state.events.length === 0) {
      return <Typography>{this.props.translate('manageRegistration.noClassesAvailable')}</Typography>;
    }

    return (
      <Typography>
        {this.state.events.map((event) => {
          let classDateTime = moment.utc(event.classDateTime);
          return (
            <Chip
              key={event.classDateTime}
              icon={this.state.selectedEvent === event ? <DoneIcon /> : undefined}
              label={classDateTime.format('HH:mm')}
              color="secondary"
              variant="outlined"
              onClick={() => {
                this.setState({
                  selectedEvent: event,
                  showAlternatives: false,
                });
              }}
            />
          );
        })}
      </Typography>
    );
  };

  renderCourseDescription = (course: Course): JSX.Element | null => {
    if (course.description) {
      return (
        <Typography component="div">
          <div dangerouslySetInnerHTML={{ __html: course.description }} />
        </Typography>
      );
    }
    return null;
  };
}

const styles = (theme: Theme) =>
  createStyles({
    typography: {
      padding: theme.spacing(2),
    },
    card: {
      display: 'flex',
    },
    media: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    cover: {
      width: 150,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    root: {
      flexGrow: 1,
      padding: theme.spacing(2),
    },
    schedule: {
      maxWidth: theme.spacing(170),
      margin: `0 auto`,
      padding: `0 ${theme.spacing(2)}px`,
    },
    rooms: {
      margin: theme.spacing(1),
      padding: `${theme.spacing(2)}px 0`,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: theme.spacing(15),
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    coursesGrid: {
      paddingTop: theme.spacing(2),
    },
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    buttons: {
      '& > *': {
        flex: 1,
      },
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
    },
    studioCard: {},
    studioCover: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    bigAvatar: {
      width: 150,
      height: 150,
    },
    timeSelect: {
      marginBottom: theme.spacing(2),
    },
    alternativeCourses: {
      marginTop: theme.spacing(2),
    },
    avatar: {},
  });

interface StoreState {
  locale: any;
  user: any;
  studio: Studio;
  domain: {
    domain: {
      id: string;
      currencyCode: string;
    };
  };
}

function mapStateToProps(state: StoreState, ownProps: RouteComponentProps) {
  let date = queryString.parse(ownProps.location.search).date as string | undefined;

  let dateMoment;
  if (!date) {
    dateMoment = moment.utc().add(1, 'days').startOf('day');
  } else {
    dateMoment = moment.utc(date, 'DD-MM-YYYY').startOf('day');
  }

  return {
    translate: getTranslate(state.locale),
    currentLanguage: getActiveLanguage(state.locale).code,
    user: state.user,
    studio: state.studio,
    domain: state.domain.domain,
    date: dateMoment,
  };
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(push(`${routes.class()}?keyword=${''}&location=${''}`));
      } else {
        dispatch(push(`${routes.classId()}?keyword=${''}`));
      }
    },
    toClassSignUp: (link: string) => {
      dispatch(push(link));
    },
    clientAttendance: (studio: Studio, groupId: string, attendanceId: string, client: { new: boolean }) => {
      dispatch(
        push(
          `${routes.clientAttendance({
            domainName: studio.domainName,
            groupId: groupId,
            id: attendanceId,
          })}${client.new ? '?new=true' : ''}`
        )
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(BookingCourseView));
